import * as styles from "./TabsIndicator.css";

import { animate } from "animejs";
import { clsx } from "clsx";
import type { JSX } from "react";
import { useEffect, useRef } from "react";

import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import { TRANSITION_NORMAL_MS } from "@frontend/styles";

import { useTabsContext } from "./Tabs.context";
import type { TabsIndicatorProps } from "./Tabs.types";

const MAGNETIC_PULL_PX = 14;

const MAGNETIC_STRETCH_SCALE = 1.05;

export function TabsIndicator({
  className,
  ...props
}: TabsIndicatorProps): JSX.Element {
  const { variant, orientation } = useTabsContext();
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    const list = el?.parentElement;

    if (!el || !list || variant !== "segmented") {
      return;
    }

    const scaleProp = orientation === "horizontal" ? "scaleX" : "scaleY";

    function pull(event: PointerEvent) {
      if (!el) {
        return;
      }

      const rect = el.getBoundingClientRect();

      const center =
        orientation === "horizontal"
          ? rect.left + rect.width / 2
          : rect.top + rect.height / 2;

      const pointerPos =
        orientation === "horizontal" ? event.clientX : event.clientY;

      const delta = Math.max(
        -MAGNETIC_PULL_PX,
        Math.min(MAGNETIC_PULL_PX, pointerPos - center),
      );

      const scale =
        1 + (Math.abs(delta) / MAGNETIC_PULL_PX) * (MAGNETIC_STRETCH_SCALE - 1);

      const towardsFarEdge = delta >= 0;

      el.style.transformOrigin =
        orientation === "horizontal"
          ? towardsFarEdge
            ? "0% 50%"
            : "100% 50%"
          : towardsFarEdge
            ? "50% 0%"
            : "50% 100%";

      animate(el, {
        [scaleProp]: scale,
        duration: TRANSITION_NORMAL_MS,
        ease: "outQuad",
      });
    }

    function release() {
      if (!el) {
        return;
      }

      list?.removeEventListener("pointermove", pull);

      animate(el, {
        [scaleProp]: 1,
        duration: TRANSITION_NORMAL_MS * 1.1,
        ease: "outElastic(1, .6)",
      });
    }

    function handlePointerDown(event: PointerEvent) {
      const tab = (event.target as HTMLElement).closest(
        '[data-slot="tabs-tab"]',
      );

      if (!tab || tab.hasAttribute("data-active")) {
        return;
      }

      pull(event);
      list?.addEventListener("pointermove", pull);
    }

    list.addEventListener("pointerdown", handlePointerDown);
    list.addEventListener("pointerup", release);
    list.addEventListener("pointerleave", release);
    list.addEventListener("pointercancel", release);

    return () => {
      list.removeEventListener("pointerdown", handlePointerDown);
      list.removeEventListener("pointerup", release);
      list.removeEventListener("pointerleave", release);
      list.removeEventListener("pointercancel", release);
      list.removeEventListener("pointermove", pull);
    };
  }, [variant, orientation]);

  return (
    <BaseTabs.Indicator
      ref={ref}
      data-slot="tabs-indicator"
      className={clsx(styles.indicator({ variant, orientation }), className)}
      {...props}
    />
  );
}
